const UTM_STORAGE_KEY = "utm_params"

const TRACKING_PARAM_KEYS = new Set([
  "gclid",
  "fbclid",
  "gad_source",
  "creative",
  "audiences",
  "description",
  "car_model",
  "gbraid",
  "wbraid",
  "msclkid",
  "ttclid",
])

const isTrackingParam = (key: string): boolean =>
  key.startsWith("utm_") || TRACKING_PARAM_KEYS.has(key)

const normalizeSearch = (search: string): string =>
  search
    .replace(/^[?#]+/, "")
    .replace(/#.*/, "")
    .trim()

const getHashQueryString = (): string => {
  if (typeof window === "undefined") return ""
  const hash = window.location.hash || ""
  const queryIndex = hash.indexOf("?")
  return queryIndex >= 0 ? hash.slice(queryIndex) : ""
}

export const getHashAnchor = (): string => {
  if (typeof window === "undefined") return ""

  const hash = window.location.hash || ""
  if (!hash) return ""

  const queryIndex = hash.indexOf("?")
  return queryIndex >= 0 ? hash.slice(0, queryIndex) : hash
}

const decodeKey = (rawKey: string): string => {
  try {
    return decodeURIComponent(rawKey.replace(/\+/g, "%20")).toLowerCase()
  } catch {
    return rawKey.toLowerCase()
  }
}

const mergeTrackingSearches = (...searches: string[]): string =>
  searches.reduce(
    (accumulator, search) =>
      mergeTrackingParams(accumulator, keepOnlyUtmParams(search)),
    ""
  )

export const persistUtmParams = (): void => {
  if (typeof window === "undefined") return
  const utmString = mergeTrackingSearches(
    getStoredUtmParams(),
    window.location.search,
    getHashQueryString()
  )
  if (utmString) {
    sessionStorage.setItem(UTM_STORAGE_KEY, utmString)
  }
}

export const getStoredUtmParams = (): string => {
  if (typeof window === "undefined") return ""
  return sessionStorage.getItem(UTM_STORAGE_KEY) || ""
}

export const mergeTrackingParams = (
  baseSearch: string,
  trackingSearch: string
): string => {
  const merged = new URLSearchParams(normalizeSearch(baseSearch))
  const trackingParams = new URLSearchParams(normalizeSearch(trackingSearch))

  for (const [key, value] of trackingParams.entries()) {
    if (!merged.has(key)) {
      merged.append(key, value)
    }
  }

  return merged.toString()
}

export const getCurrentTrackingSearch = (): string => {
  if (typeof window === "undefined") return ""

  return mergeTrackingSearches(
    window.location.search,
    getHashQueryString(),
    getStoredUtmParams()
  )
}

export const keepOnlyUtmParams = (search: string): string => {
  if (!search) return ""

  return normalizeSearch(search)
    .split("&")
    .filter(Boolean)
    .filter((param) => {
      const separatorIndex = param.indexOf("=")
      const rawKey =
        separatorIndex === -1 ? param : param.slice(0, separatorIndex)
      const key = decodeKey(rawKey)
      return isTrackingParam(key)
    })
    .join("&")
}

export const getUtmParamsAsKeyValueList = (): {
  key: string
  value: string
}[] => {
  if (typeof window === "undefined") return []

  const search = getCurrentTrackingSearch()
  if (!search) return []

  const parsedParams = new URLSearchParams(search)
  return Array.from(parsedParams.entries()).map(([key, value]) => ({
    key,
    value,
  }))
}
